import { Component } from "react";
import { CURRENT_YEAR } from "@/config/constants";
import { IR_AUTO_INSURANCE_DEFAULT_PATH } from "@/config/site/insuranceranked";
import * as selectors from "@/selectors";
import { fetchMediaAlphaFeed } from "@/store/mediaAlpha/actions";
import setMultistepModalState from "@/store/multistepModal/actions";
import PropTypes, { oneOfType } from "prop-types";
import { mergeLeft, prop } from "ramda";
import { Label, ProgressBar } from "react-aria-components";
import { default as ReactModal } from "react-modal2";
import { connect } from "react-redux";
import ScrollLock from "react-scrolllock";
import smoothscroll from "smoothscroll-polyfill";
import tw, { css } from "twin.macro";
import useSSR from "use-ssr";

import { LeftArrowIcon } from "../../../../client/assets/icons/common/LeftArrowIcon";
import withMediaAlphaData from "../../../HighOrderComponents/withMediaAlphaData";
import Accidents from "./steps/Accidents";
import Age from "./steps/Age";
import CreditScore from "./steps/CreditScore";
import CurrentlyInsured from "./steps/CurrentlyInsured";
import HomeOwnership from "./steps/HomeOwnership";
import MilitaryAffiliation from "./steps/MilitaryAffiliation";
import Vehicles from "./steps/Vehicles";
import ZipCode from "./steps/ZipCode";

const PERCENTAGE_MULTIPLIER = 100;

const { isServer } = useSSR();
if (!isServer) {
	smoothscroll.polyfill();
}

/* global NOT_PROD TESTING */

const shouldFirePageViews = !NOT_PROD || TESTING;

// Define functional components

const componentMap = {
	shared: {
		zip: ZipCode,
	},
	verticalSpecific: {
		[IR_AUTO_INSURANCE_DEFAULT_PATH]: [
			CurrentlyInsured,
			HomeOwnership,
			MilitaryAffiliation,
			Age,
			Vehicles,
			Accidents,
			CreditScore,
		],
	},
};

const mapDispatchToProps = {
	fetchMediaAlphaFeed,
	setMultistepModalState,
};

const mapStateToProps = (state) => ({
	vertical: selectors.getVertical(state),
	usesMultiStepModal: selectors.getUsesMultiStepModal(state),
	multiStepConfig: selectors.getMultiStepModalConfig(state),
	isTargetedPath: selectors.getIsTargetedPath(state),
});

export class AutoInsuranceModal extends Component {
	state = {
		hasModalBeenClosed: false,
		mediaAlphaData: {},
		currentStep: 0,
		completedSteps: {},
	};

	fetchFeedAndClose = (mediaAlphaData) => {
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: "smooth",
		});
		if (mediaAlphaData) {
			this.props.fetchMediaAlphaFeed(this.props.vertical, mediaAlphaData);
		} else {
			this.props.fetchMediaAlphaFeed(
				this.props.vertical,
				this.state.mediaAlphaData,
			);
		}
		this.closeModal();
	};

	closeModal = () => {
		this.setState({ hasModalBeenClosed: true });
	};

	closeBeforeComplete = () => {
		const { currentStep } = this.state;
		const { setMultistepModalState } = this.props;
		const defaultMediaAlphaData = this.props.mediaAlphaData;
		const defaultMediaAlphaDataProps = defaultMediaAlphaData.data;
		const data = {
			data: {
				...defaultMediaAlphaDataProps,
				...this.state.mediaAlphaData.data,
			},
		};
		const mergedData = { ...this.props.mediaAlphaData, ...data };
		/* send a standard pageview so clicks aren't associated with the virtual pageviews of the modal*/
		window.gtag("event", "page_view");
		setMultistepModalState(mergedData);
		if (currentStep === 0) {
			/* If they bail on the first step, send the default MA data from the withMediaAlpha HOC*/
			this.fetchFeedAndClose(this.props.mediaAlphaData);
			/* Otherwise send the actual data the user input */
		} else this.fetchFeedAndClose(this.state.mediaAlphaData);
	};

	componentDidMount() {
		const { usesMultiStepModal, multiStepConfig, vertical, isTargetedPath } =
			this.props;
		if (usesMultiStepModal && multiStepConfig && shouldFirePageViews) {
			const pathTypeIdentifier = isTargetedPath ? "targeted" : "non-targeted";
			window.gtag("event", "page_view", {
				page_path: `/${vertical}/${pathTypeIdentifier}-multistep-modal/v2/1`,
			});
		}
	}

	setMediaAlphaState = (mediaAlphaDataProp, isLastStep) => {
		const { vertical, isTargetedPath, setMultistepModalState } = this.props;
		const { currentStep, completedSteps } = this.state;
		const pathTypeIdentifier = isTargetedPath ? "targeted" : "non-targeted";
		const defaultMediaAlphaData = this.props.mediaAlphaData;
		const defaultMediaAlphaDataProps = defaultMediaAlphaData.data;
		const data = {
			data: {
				...defaultMediaAlphaDataProps,
				...mergeLeft(mediaAlphaDataProp, this.state.mediaAlphaData.data),
			},
		};
		const mergedData = { ...this.props.mediaAlphaData, ...data };
		const isStepCompleted = prop(currentStep, completedSteps);
		if (isLastStep) {
			this.setState(
				(prevState) => ({
					mediaAlphaData: mergedData,
					completedSteps: { ...prevState.completedSteps, [currentStep]: true },
				}),
				() => {
					if (shouldFirePageViews && !isStepCompleted) {
						window.gtag("event", "page_view", {
							page_path: `/${vertical}/${pathTypeIdentifier}-multistep-modal/v2/completed`,
						});
						window.gtag("event", "page_view");
					}
					this.fetchFeedAndClose();
					setMultistepModalState(this.state.mediaAlphaData);
				},
			);
		} else {
			const upcomingStep = currentStep + 1;
			const pageviewStep = upcomingStep + 1;
			this.setState(
				(prevState) => ({
					mediaAlphaData: mergedData,
					currentStep: upcomingStep,
					completedSteps: { ...prevState.completedSteps, [currentStep]: true },
				}),
				() => {
					if (shouldFirePageViews && !isStepCompleted) {
						window.gtag("event", "page_view", {
							page_path: `/${vertical}/${pathTypeIdentifier}-multistep-modal/v2/${pageviewStep}`,
						});
					}
				},
			);
		}
	};

	showPreviousStep(previousStep) {
		this.setState({ currentStep: previousStep });
	}

	render() {
		const { usesMultiStepModal, multiStepConfig, vertical } = this.props;
		const { hasModalBeenClosed, currentStep } = this.state;

		// exit early when config isn't defined
		if (!multiStepConfig) return null;

		const commonStepComponents = multiStepConfig.map(
			(key) => componentMap.shared[key],
		);
		const verticalSpecificStepComponent = prop(
			vertical,
			componentMap.verticalSpecific,
		);
		const stepComponents = verticalSpecificStepComponent
			? [...verticalSpecificStepComponent, ...commonStepComponents]
			: commonStepComponents;
		const isFirstStep = currentStep === 0;
		const isLastStep = stepComponents[currentStep + 1] === undefined;
		const CurrentComponent = stepComponents[currentStep];

		const isOpen = !hasModalBeenClosed && usesMultiStepModal;

		return isOpen ? (
			<>
				<ScrollLock isActive />
				<ReactModal
					css={[
						tw`border-none rounded-lg max-w-xs sm:max-w-md m-auto flex items-center`,
						css`
							inset: 40px;
						`,
					]}
					onClose={this.closeBeforeComplete}
					closeOnBackdropClick={false}
					backdropStyles={{
						backgroundColor: "rgba(0, 0, 0, 0.6)",
						transition: "opacity .15s ease-out",
						zIndex: 100,
						display: "flex",
						position: "fixed",
						width: "100%",
						height: "100%",
						top: 0,
						left: 0,
					}}
					closeOnEsc={false}
				>
					<div
						id="multiStepModal"
						css={[
							tw`relative bg-white w-full rounded-lg max-w-xs sm:max-w-md`,
							css`
								position: absolute;
								top: 50%;
								left: 50%;
								transform: translate(-50%, -50%);
							`,
						]}
					>
						<div>
							<div css={tw`flex items-center`}>
								<div
									css={tw`text-left font-bold text-black md:text-xl pl-8 pt-8 leading-6 md:leading-7`}
								>
									{`Let's Find You ${CURRENT_YEAR}'s Best Auto Insurance Rates!`}
									<div
										css={css`
											position: absolute;
											left: 28px;
											top: 12px;
										`}
									>
										<svg
											width="95"
											height="20"
											viewBox="0 0 95 20"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M0 10C0 4.47715 4.47715 0 10 0H85C90.5228 0 95 4.47715 95 10C95 15.5228 90.5228 20 85 20H10C4.47715 20 0 15.5228 0 10Z"
												fill="#6366F1"
											/>
											<path
												d="M26.443 14.121C28.357 14.121 29.49 13.186 29.49 11.712C29.49 10.711 28.874 10.062 27.961 9.864C28.742 9.589 29.259 9.039 29.259 8.159C29.259 6.927 28.247 6.025 26.52 6.025C24.727 6.025 23.704 7.081 23.616 8.522H25.189C25.277 7.785 25.739 7.334 26.487 7.334C27.213 7.334 27.576 7.752 27.576 8.313C27.576 8.984 27.202 9.358 26.377 9.358H25.805V10.546H26.421C27.268 10.546 27.686 10.931 27.686 11.679C27.686 12.383 27.268 12.812 26.377 12.812C25.431 12.812 25.024 12.262 24.958 11.503H23.352C23.418 13.098 24.441 14.121 26.443 14.121ZM33.9424 12.724C32.8424 12.724 32.2594 11.899 32.2594 10.249V9.875C32.2594 8.247 32.8204 7.4 33.9204 7.4C35.0094 7.4 35.5594 8.291 35.5594 9.875V10.227C35.5594 11.877 35.0534 12.724 33.9424 12.724ZM33.8984 14.121C36.0214 14.121 37.3304 12.625 37.3304 10.216V9.853C37.3304 7.411 35.9334 6.025 33.9094 6.025C31.8854 6.025 30.4774 7.466 30.4774 9.886V10.26C30.4774 12.669 31.8084 14.121 33.8984 14.121ZM43.8597 14.121C45.7187 14.121 46.8627 13.01 46.8627 11.613C46.8627 9.985 45.8837 9.468 43.9807 9.226C42.7707 9.05 42.5177 8.786 42.5177 8.225C42.5177 7.697 42.9137 7.334 43.6947 7.334C44.4757 7.334 44.8937 7.664 45.0037 8.379H46.6427C46.4997 6.817 45.4657 6.025 43.6947 6.025C41.9567 6.025 40.8347 7.026 40.8347 8.368C40.8347 9.886 41.6597 10.491 43.6727 10.755C44.8277 10.931 45.1577 11.151 45.1577 11.778C45.1577 12.394 44.6407 12.823 43.8597 12.823C42.6937 12.823 42.3967 12.24 42.3197 11.536H40.6147C40.7137 13.109 41.7037 14.121 43.8597 14.121ZM50.6538 14.121C52.2268 14.121 53.2168 13.428 53.4038 12.185H51.8968C51.7978 12.669 51.4348 12.988 50.6978 12.988C49.8288 12.988 49.3118 12.438 49.2678 11.481H53.4148V11.041C53.4148 8.984 52.0948 8.115 50.6098 8.115C48.9378 8.115 47.6508 9.292 47.6508 11.107V11.195C47.6508 13.032 48.9158 14.121 50.6538 14.121ZM49.2898 10.502C49.4108 9.688 49.8948 9.215 50.6098 9.215C51.3688 9.215 51.8088 9.633 51.8638 10.502H49.2898ZM57.0659 14.121C58.7379 14.121 59.7279 13.175 59.8159 11.811H58.3749C58.2979 12.548 57.7809 12.922 57.1429 12.922C56.2519 12.922 55.7349 12.328 55.7349 11.173V11.085C55.7349 9.985 56.2849 9.369 57.1099 9.369C57.7589 9.369 58.1659 9.677 58.2759 10.348H59.7829C59.6399 8.698 58.4079 8.115 57.0769 8.115C55.4379 8.115 54.1069 9.248 54.1069 11.107V11.195C54.1069 13.076 55.3829 14.121 57.0659 14.121ZM63.5023 12.911C62.6003 12.911 62.0943 12.251 62.0943 11.151V11.063C62.0943 9.963 62.6223 9.325 63.5023 9.325C64.3933 9.325 64.9103 9.985 64.9103 11.085V11.162C64.9103 12.251 64.3933 12.911 63.5023 12.911ZM63.4913 14.121C65.2183 14.121 66.5273 12.966 66.5273 11.151V11.063C66.5273 9.27 65.2293 8.115 63.5023 8.115C61.7753 8.115 60.4663 9.292 60.4663 11.096V11.184C60.4663 12.977 61.7753 14.121 63.4913 14.121ZM67.5966 14H69.1916V10.678C69.1916 9.831 69.7086 9.413 70.3906 9.413C71.0946 9.413 71.4026 9.787 71.4026 10.568V14H72.9976V10.337C72.9976 8.797 72.1946 8.115 71.0616 8.115C70.1046 8.115 69.4776 8.588 69.1916 9.16V8.247H67.5966V14ZM76.5202 14.121C77.3672 14.121 78.0712 13.626 78.3682 13.043V14H79.9522V5.64H78.3682V9.105C78.0492 8.522 77.4772 8.115 76.5532 8.115C75.1122 8.115 74.0232 9.226 74.0232 11.118V11.206C74.0232 13.12 75.1232 14.121 76.5202 14.121ZM76.9932 12.878C76.2012 12.878 75.6512 12.317 75.6512 11.173V11.085C75.6512 9.941 76.1462 9.336 77.0372 9.336C77.9062 9.336 78.4122 9.919 78.4122 11.063V11.151C78.4122 12.317 77.8292 12.878 76.9932 12.878ZM83.4581 14.121C84.9761 14.121 85.8781 13.472 85.8781 12.229C85.8781 10.997 85.0861 10.634 83.6451 10.425C82.8091 10.304 82.5341 10.161 82.5341 9.798C82.5341 9.446 82.8311 9.204 83.3481 9.204C83.8981 9.204 84.1621 9.424 84.2501 9.897H85.7131C85.5701 8.577 84.6131 8.115 83.3371 8.115C82.1601 8.115 81.0601 8.709 81.0601 9.908C81.0601 11.03 81.6541 11.47 83.1831 11.701C84.0081 11.822 84.3491 11.987 84.3491 12.383C84.3491 12.779 84.0631 13.01 83.4471 13.01C82.7541 13.01 82.5011 12.713 82.4351 12.196H80.9501C81.0051 13.439 81.9071 14.121 83.4581 14.121Z"
												fill="white"
											/>
											<circle cx="10" cy="10" r="7" fill="white" />
											<path
												d="M10 15C11.3261 15 12.5979 14.4732 13.5355 13.5355C14.4732 12.5979 15 11.3261 15 10C15 8.67392 14.4732 7.40215 13.5355 6.46447C12.5979 5.52678 11.3261 5 10 5L10 10V15Z"
												fill="#6366F1"
											/>
										</svg>
									</div>
								</div>
								<img
									src="/react-images/insuranceranked/bmwMontage.svg"
									alt="car image"
								/>
							</div>
							{/* Progress Bar */}
							<ProgressBar
								className="tw-relative"
								aria-label="Modal Progress"
								value={
									((currentStep + 1) / stepComponents.length) *
									PERCENTAGE_MULTIPLIER
								}
							>
								{({ percentage }) => (
									<>
										<Label className="sr-only">Modal Progress</Label>
										<div className="tw-relative tw-h-1 tw-overflow-hidden tw-bg-[#D9DCE1]">
											<div
												className="tw-absolute tw-left-0 tw-top-0 tw-h-full tw-rounded-r-sm tw-bg-[#FF5161] tw-transition-all tw-duration-300"
												style={{ width: percentage + "%" }}
											/>
										</div>
									</>
								)}
							</ProgressBar>
						</div>
						<div
							id="autoInsuranceModalSteps"
							css={tw`flex flex-col content-center items-center relative bg-ir-multiStep-modal rounded-lg`}
						>
							<div css={tw`max-w-sm w-full m-auto pl-4 pr-4 pt-4`}>
								<CurrentComponent
									setMediaAlphaState={this.setMediaAlphaState}
									isLastStep={isLastStep}
								/>
								<div
									css={
										isFirstStep
											? tw`text-center text-xs text-ir-modal-disclaimer mt-5 mb-5`
											: tw` mb-5`
									}
								>
									{isFirstStep ? (
										<div>No contact information or SSN needed.</div>
									) : (
										<div
											css={css`
												border-top: 1px solid #d9dce1;
											`}
										>
											<div
												css={tw`flex items-center justify-center text-sm cursor-pointer font-semibold text-center mt-4`}
												onClick={() => this.showPreviousStep(currentStep - 1)}
											>
												<LeftArrowIcon className="tw-text-[26px]" />
												<span>&nbsp; PREVIOUS QUESTION</span>
											</div>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</ReactModal>
			</>
		) : null;
	}
}

const NewMultiStepModal = withMediaAlphaData(
	connect(mapStateToProps, mapDispatchToProps)(AutoInsuranceModal),
);
export default NewMultiStepModal;

AutoInsuranceModal.propTypes = {
	appElement: PropTypes.string.isRequired,
	vertical: PropTypes.string.isRequired,
	mediaAlphaData: PropTypes.object.isRequired,
	fetchMediaAlphaFeed: PropTypes.func.isRequired,
	setMultistepModalState: PropTypes.func.isRequired,
	isTargetedPath: PropTypes.bool.isRequired,
	multiStepConfig: oneOfType([PropTypes.array, PropTypes.bool]),
	usesMultiStepModal: PropTypes.bool.isRequired,
};
